import { useCallback } from 'react';

import { isApiError } from 'scenes/common/helpers/apiData';
import campaignApi from 'scenes/common/api/campaign';

import {
  MotorApiResponses,
  MotorFormAnswer,
  MotorFunnelState,
} from 'scenes/motor/types';
import analytics from 'scenes/motor/analytics';
import {
  setColesCampaignCode,
  setPrefilledCampaignId,
  setPrefilledPromoCode,
} from 'scenes/motor/actions';

export const useCampaignActions = (
  funnelState: MotorFunnelState,
  onApiResponse: (data: Partial<MotorApiResponses>) => void,
  handleAnswer: (f: MotorFormAnswer) => void
) => {
  const handleCampaignResponse = useCallback(
    (campaign) => {
      onApiResponse({
        campaign,
      });
      return campaign;
    },
    [onApiResponse]
  );

  const onRetrieveCampaign = useCallback(
    (campaignId: string) => {
      if (campaignId?.length) {
        handleAnswer(setPrefilledCampaignId(campaignId));
        campaignApi()
          .retrieveCampaign(campaignId)
          .then((campaignData) => {
            if (isApiError(campaignData)) {
              analytics().error(
                funnelState,
                `Marketing campaign: ${
                  campaignData.errorReason?.message || ''
                }`,
                campaignData.errorReason?.type
              );
            }
            handleCampaignResponse(campaignData);
          });
      }
    },
    [funnelState, handleAnswer, handleCampaignResponse]
  );

  const onSetPrefilledPromoCode = useCallback(
    (pid: string) => {
      handleAnswer(setPrefilledPromoCode(pid));
    },
    [handleAnswer]
  );

  const onSetColesCampaignCode = useCallback(
    (cid: string) => {
      handleAnswer(setColesCampaignCode(cid));
    },
    [handleAnswer]
  );

  return {
    onRetrieveCampaign,
    onSetPrefilledPromoCode,
    onSetColesCampaignCode,
  };
};
